import CopyrightIcon from "@mui/icons-material/Copyright";
import { Fragment, useEffect, useRef, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { useReducedMotion } from "./ui/useReducedMotion";
import { Dialog, DialogContent, Grow } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useAuth } from "../../context/AuthContext";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CircularProgress from "@mui/material/CircularProgress";
import CheckIcon from "@mui/icons-material/Check";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import LaptopIcon from "@mui/icons-material/Laptop";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import TabletIcon from "@mui/icons-material/Tablet";
import DevicesIcon from "@mui/icons-material/Devices";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import CloudDoneOutlinedIcon from "@mui/icons-material/CloudDoneOutlined";
import {
  ThemeScope, Button, Input, Label, Card, CardHeader, CardTitle, CardDescription,
  CardContent, CardFooter, Alert, Badge, Separator,
} from "./ui/shadcn";

const MotionButton = motion.create(Button);
const MotionCard = motion.create(Card);
const MotionForm = motion.form;
const MotionDiv = motion.div;

// shadcn tokens for this concept: slate surfaces + amber accent
const themeVars = {
  "--background": "#ffffff",
  "--foreground": "#0f172a",
  "--card": "#ffffff",
  "--card-foreground": "#0f172a",
  "--primary": "#f59e0b",
  "--primary-foreground": "#0f172a",
  "--secondary": "#f1f5f9",
  "--secondary-foreground": "#334155",
  "--muted": "#f1f5f9",
  "--muted-foreground": "#64748b",
  "--accent": "#fffbeb",
  "--destructive": "#dc2626",
  "--destructive-foreground": "#ffffff",
  "--border": "#e2e8f0",
  "--input": "#cbd5e1",
  "--ring": "#d97706",
  "--radius": "0.875rem",
};

const EASE = [0.22, 1, 0.36, 1];

const STEPS = [
  { label: "Identify", icon: <PersonOutlineIcon sx={{ fontSize: 17 }} /> },
  { label: "Verify", icon: <LockOutlinedIcon sx={{ fontSize: 16 }} /> },
  { label: "Organization", icon: <BusinessOutlinedIcon sx={{ fontSize: 17 }} /> },
];

const TRUST = [
  { icon: <LockOutlinedIcon />, title: "Secure by design", text: "Encrypted sessions, role-based access and a full audit trail." },
  { icon: <ReceiptLongOutlinedIcon />, title: "GST-ready", text: "E-invoicing, e-way bills and GST filings built right in." },
  { icon: <CloudDoneOutlinedIcon />, title: "Cloud-native", text: "Always on and in sync, on every device you use." },
];

const STEP_TEXT = {
  1: { title: "Sign in to ReonBook", sub: "Enter your username, email or mobile number to get started." },
  2: { title: "Welcome back", sub: "Enter your password to access your dashboard." },
  3: { title: "Choose organization", sub: "Select the organization you would like to open." },
};

const CSS = [
  "@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&display=swap');",
  ".ld09-root, .ld09-root *, .ld09-dlg, .ld09-dlg * { font-family: 'IBM Plex Sans', -apple-system, 'Segoe UI', Roboto, sans-serif !important; }",
  "@keyframes ld09Drift { from { background-position: 0 0; } to { background-position: 24px 24px; } }",
  "@media (prefers-reduced-motion: reduce) { .ld09-root *, .ld09-root *::before, .ld09-root *::after, .ld09-dlg *, .ld09-dlg *::before, .ld09-dlg *::after { animation-duration: .001ms !important; animation-iteration-count: 1 !important; transition-duration: .001ms !important; } }",

  /* page shell */
  ".ld09-root { position: relative; width: 100%; height: 100vh; height: 100dvh; overflow-x: hidden; overflow-y: auto; display: flex; flex-direction: column; background: #0b1220; }",
  ".ld09-bg { position: fixed; inset: 0; z-index: 0; pointer-events: none; overflow: hidden; background: linear-gradient(160deg, #0b1220 0%, #111c33 55%, #0f172a 100%); }",
  ".ld09-dots { position: absolute; inset: 0; background-image: radial-gradient(rgba(148,163,184,.26) 1.2px, transparent 1.5px); background-size: 24px 24px; -webkit-mask-image: radial-gradient(ellipse 85% 75% at 50% 42%, #000 25%, transparent 100%); mask-image: radial-gradient(ellipse 85% 75% at 50% 42%, #000 25%, transparent 100%); animation: ld09Drift 46s linear infinite; }",
  ".ld09-dots-amber { position: absolute; inset: 0; background-image: radial-gradient(rgba(251,191,36,.9) 1.6px, transparent 2px); background-size: 96px 96px; background-position: 12px 12px; -webkit-mask-image: radial-gradient(ellipse 70% 60% at 50% 40%, #000 20%, transparent 100%); mask-image: radial-gradient(ellipse 70% 60% at 50% 40%, #000 20%, transparent 100%); }",
  ".ld09-glow-a { position: absolute; top: -18%; left: 50%; margin-left: -340px; width: 680px; height: 680px; border-radius: 50%; background: radial-gradient(circle, rgba(245,158,11,.14) 0%, transparent 66%); }",
  ".ld09-glow-b { position: absolute; bottom: -22%; right: -12%; width: 560px; height: 560px; border-radius: 50%; background: radial-gradient(circle, rgba(71,85,105,.38) 0%, transparent 68%); }",
  ".ld09-content { position: relative; z-index: 1; flex-shrink: 0; margin: auto; width: 100%; max-width: 800px; display: flex; flex-direction: column; align-items: center; padding: 32px 16px 20px; }",
  "@media (min-width: 600px) { .ld09-content { padding: 40px 24px 28px; } }",
  ".ld09-sr { position: absolute; width: 1px; height: 1px; margin: -1px; padding: 0; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0; }",

  /* brand */
  ".ld09-brand { text-align: center; margin-bottom: 22px; }",
  ".ld09-brand img { display: block; margin: 0 auto; height: 38px; max-width: 70vw; filter: brightness(0) invert(1); opacity: .95; }",
  "@media (min-width: 600px) { .ld09-brand { margin-bottom: 26px; } .ld09-brand img { height: 46px; } }",
  ".ld09-eyebrow { margin: 10px 0 0; color: #fbbf24; font-size: .68rem; font-weight: 700; letter-spacing: 2.6px; text-transform: uppercase; }",

  /* card */
  ".ld09-root .ld09-card { position: relative; z-index: 1; flex-shrink: 0; width: 100%; max-width: 420px; overflow: hidden; border: 0; border-radius: 22px; background: #fff; box-shadow: 0 30px 70px -18px rgba(0,0,0,.65), 0 0 0 1px rgba(255,255,255,.08); }",
  ".ld09-accent { pointer-events: none; position: absolute; top: 0; left: 0; right: 0; height: 4px; background: linear-gradient(90deg, #f59e0b 0%, #fbbf24 55%, rgba(251,191,36,0) 100%); }",
  ".ld09-bar { pointer-events: none; position: absolute; top: 0; left: 0; right: 0; height: 4px; overflow: hidden; background: #fde68a; }",
  ".ld09-bar > div { width: 32%; height: 100%; background: #d97706; border-radius: 4px; }",
  ".ld09-steps { padding: 30px 20px 0; }",
  ".ld09-root .ld09-card .ui-card-header { padding: 22px 20px 0; gap: .5rem; }",
  ".ld09-root .ld09-card .ui-card-title { font-size: clamp(1.25rem, 5vw, 1.45rem); line-height: 1.2; overflow-wrap: anywhere; font-weight: 800; letter-spacing: -.5px; color: #0f172a; }",
  ".ld09-root .ld09-card .ui-card-description { font-size: .88rem; line-height: 1.6; color: #64748b; }",
  ".ld09-root .ld09-card .ui-card-content { padding: 22px 20px 0; }",
  ".ld09-root .ld09-card .ui-card-footer { padding: 0 20px 26px; flex-direction: column; align-items: stretch; }",
  "@media (min-width: 600px) { .ld09-steps { padding: 34px 32px 0; } .ld09-root .ld09-card .ui-card-header { padding: 24px 32px 0; } .ld09-root .ld09-card .ui-card-title { font-size: 1.65rem; } .ld09-root .ld09-card .ui-card-content { padding: 24px 32px 0; } .ld09-root .ld09-card .ui-card-footer { padding: 0 32px 28px; } }",

  /* step indicator */
  ".ld09-stepper { list-style: none; margin: 0; padding: 0; display: flex; align-items: flex-start; }",
  ".ld09-step { display: flex; flex-direction: column; align-items: center; gap: 6px; width: 66px; flex-shrink: 0; }",
  "@media (min-width: 600px) { .ld09-step { width: 78px; } }",
  ".ld09-node { position: relative; width: 34px; height: 34px; border-radius: 50%; border: 2px solid; display: flex; align-items: center; justify-content: center; }",
  ".ld09-ring { position: absolute; inset: -2px; border-radius: 50%; border: 2px solid #f59e0b; pointer-events: none; }",
  ".ld09-step-label { font-size: .66rem; font-weight: 600; color: #64748b; text-align: center; line-height: 1.2; letter-spacing: .2px; transition: color .3s ease; }",
  "@media (min-width: 600px) { .ld09-step-label { font-size: .72rem; } }",
  ".ld09-step[data-state='active'] .ld09-step-label { color: #0f172a; font-weight: 800; }",
  ".ld09-step[data-state='done'] .ld09-step-label { color: #334155; }",
  ".ld09-conn { flex: 1; min-width: 12px; height: 3px; margin-top: 15px; border-radius: 3px; background: #e2e8f0; position: relative; overflow: hidden; list-style: none; }",
  ".ld09-conn-fill { position: absolute; inset: 0; background: #f59e0b; transform-origin: left center; }",

  /* fields */
  ".ld09-panel { display: block; margin: 0; }",
  ".ld09-field { display: flex; flex-direction: column; gap: 8px; margin-bottom: 20px; }",
  ".ld09-root .ld09-field .ui-label { font-size: .8rem; font-weight: 700; color: #334155; letter-spacing: .1px; }",
  ".ld09-root .ld09-input { height: 3rem; border-radius: 12px; background: #f1f5f9; border: 1.5px solid #8191a7; color: #0f172a; -webkit-text-fill-color: #0f172a; caret-color: #0f172a; opacity: 1; font-size: .92rem; font-weight: 500; padding: 0 14px; }",
  ".ld09-root .ld09-input::placeholder { color: #64748b; -webkit-text-fill-color: #64748b; opacity: 1; }",
  ".ld09-root .ld09-input:hover { border-color: #475569; }",
  ".ld09-root .ld09-input:focus-visible { border-color: #d97706; background: #fff; }",
  ".ld09-root .ld09-input:-webkit-autofill, .ld09-root .ld09-input:-webkit-autofill:hover { -webkit-text-fill-color: #0f172a; -webkit-box-shadow: 0 0 0 1000px #f1f5f9 inset; }",
  ".ld09-root .ld09-input:-webkit-autofill:focus { -webkit-text-fill-color: #0f172a; -webkit-box-shadow: 0 0 0 1000px #fff inset, 0 0 0 2px #fff, 0 0 0 4px #d97706; }",
  ".ld09-pw { position: relative; }",
  ".ld09-root .ld09-eye { position: absolute; top: 2px; right: 2px; height: 2.75rem; width: 2.75rem; color: #64748b; border-radius: 10px; }",
  ".ld09-root .ld09-eye:hover { color: #0f172a; background: #f1f5f9; }",
  ".ld09-capsicon { pointer-events: none; position: absolute; top: 50%; right: 52px; transform: translateY(-50%); color: #b45309; display: flex; }",
  ".ld09-caps { margin: 0; display: inline-flex; align-items: center; gap: 4px; color: #b45309; font-size: .75rem; font-weight: 700; }",
  ".ld09-row-end { display: flex; justify-content: flex-end; margin: -6px 0 20px; }",
  ".ld09-link { display: inline-flex; align-items: center; min-height: 44px; color: #b45309; font-weight: 700; text-decoration: none; }",
  ".ld09-link:hover { text-decoration: underline; text-underline-offset: 3px; }",
  ".ld09-link:focus-visible { outline: 2px solid #d97706; outline-offset: 3px; border-radius: 4px; }",

  /* buttons */
  ".ld09-root .ui-btn, .ld09-dlg .ui-btn { transition: background-color .15s, box-shadow .15s, opacity .15s, color .15s, border-color .15s; }",
  ".ld09-root .ui-btn:active, .ld09-dlg .ui-btn:active { transform: none; }",
  ".ld09-root .ld09-cta { width: 100%; height: 3.1rem; border-radius: 12px; font-size: .95rem; font-weight: 800; text-transform: none; background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%); color: #0f172a; box-shadow: 0 10px 22px -8px rgba(245,158,11,.65); }",
  ".ld09-root .ld09-cta:hover { opacity: 1; background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); box-shadow: 0 14px 28px -8px rgba(217,119,6,.65); }",
  ".ld09-root .ld09-cta:disabled { opacity: 1; background: #fde68a; color: #78350f; box-shadow: none; }",
  ".ld09-root .ld09-ghost { width: 100%; height: 2.9rem; margin-top: 12px; border-radius: 12px; font-weight: 700; color: #334155; border-color: #cbd5e1; }",
  ".ld09-root .ld09-ghost:hover { border-color: #f59e0b; background: #fffbeb; }",
  ".ld09-root .ld09-textbtn { width: 100%; height: 2.75rem; font-size: .82rem; font-weight: 700; color: #64748b; border-radius: 10px; }",
  ".ld09-root .ld09-textbtn:hover { background: #f1f5f9; color: #0f172a; }",
  ".ld09-root .ld09-change { height: 2.75rem; min-width: 44px; padding: 0 12px; border-radius: 999px; font-size: .78rem; font-weight: 700; color: #b45309; flex-shrink: 0; }",
  ".ld09-root .ld09-change:hover { background: #fef3c7; }",
  ".ld09-signup { margin: 18px 0 0; text-align: center; color: #64748b; font-size: .875rem; }",

  /* identity pill */
  ".ld09-root .ld09-pill { display: flex; width: 100%; align-items: center; gap: 8px; margin-bottom: 20px; padding: 6px 6px 6px 8px; border-radius: 999px; border-color: #e2e8f0; background: #f8fafc; color: #0f172a; font-size: .88rem; font-weight: 600; min-width: 0; }",
  ".ld09-pill-ico { width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; background: #e2e8f0; color: #334155; }",
  ".ld09-pill-ico[data-ok='true'] { background: #fef3c7; color: #b45309; }",
  ".ld09-pill-txt { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }",

  /* error alert */
  ".ld09-root .ld09-alert { display: flex; align-items: flex-start; gap: 8px; background: #fef2f2; border-color: #fecaca; color: #b91c1c; border-radius: 12px; padding: 12px 14px; font-size: .83rem; line-height: 1.5; overflow-wrap: anywhere; min-width: 0; }",
  ".ld09-alert-wrap { padding-bottom: 20px; }",

  /* organizations */
  ".ld09-orgs { display: flex; flex-direction: column; gap: 10px; max-height: 270px; overflow-y: auto; padding: 4px 4px 4px 2px; margin: -4px -4px 8px -2px; }",
  ".ld09-org { position: relative; display: block; cursor: pointer; }",
  ".ld09-root .ld09-org-card { display: flex; align-items: center; gap: 12px; padding: 12px 14px; border-radius: 14px; border: 1.5px solid #e2e8f0; background: #f8fafc; box-shadow: none; transition: border-color .2s ease, background-color .2s ease, box-shadow .2s ease; }",
  ".ld09-org:hover .ld09-org-card { border-color: #fbbf24; }",
  ".ld09-org[data-checked='true'] .ld09-org-card { border-color: #f59e0b; background: #fffbeb; box-shadow: 0 8px 20px -10px rgba(245,158,11,.6); }",
  ".ld09-org:focus-within .ld09-org-card { box-shadow: 0 0 0 2px #fff, 0 0 0 4px #d97706; }",
  ".ld09-org-ico { width: 38px; height: 38px; border-radius: 12px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; background: #e2e8f0; color: #334155; transition: background-color .2s ease, color .2s ease; }",
  ".ld09-org[data-checked='true'] .ld09-org-ico { background: #f59e0b; color: #0f172a; }",
  ".ld09-org-name { flex: 1; min-width: 0; font-size: .9rem; font-weight: 700; color: #0f172a; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }",
  ".ld09-org-check { width: 22px; height: 22px; border-radius: 50%; flex-shrink: 0; display: flex; align-items: center; justify-content: center; border: 1.5px solid #cbd5e1; color: transparent; }",
  ".ld09-org[data-checked='true'] .ld09-org-check { background: #0f172a; border-color: #0f172a; color: #fbbf24; }",
  ".ld09-hint { margin: 0 0 20px 2px; color: #64748b; font-size: .76rem; }",

  /* trust strip */
  ".ld09-root .ld09-strip { flex-shrink: 0; margin-top: 22px; width: 100%; max-width: 780px; display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); overflow: hidden; border-radius: 22px; border: 1px solid rgba(148,163,184,.2); background: rgba(255,255,255,.045); color: #f1f5f9; -webkit-backdrop-filter: blur(6px); backdrop-filter: blur(6px); box-shadow: 0 20px 50px -24px rgba(0,0,0,.6); }",
  ".ld09-tile { display: flex; flex-direction: column; align-items: center; text-align: center; gap: 8px; padding: 16px 6px; transition: background-color .25s ease; }",
  ".ld09-tile + .ld09-tile { border-left: 1px solid rgba(148,163,184,.18); }",
  ".ld09-tile:hover { background: rgba(255,255,255,.05); }",
  ".ld09-tile-ico { width: 42px; height: 42px; flex-shrink: 0; border-radius: 14px; display: flex; align-items: center; justify-content: center; color: #fbbf24; background: rgba(245,158,11,.12); border: 1px solid rgba(245,158,11,.38); }",
  ".ld09-tile-ico svg { font-size: 22px; }",
  ".ld09-tile-title { margin: 0; color: #f1f5f9; font-weight: 800; font-size: .76rem; line-height: 1.3; }",
  ".ld09-tile-text { display: none; margin: 4px 0 0; color: #94a3b8; font-size: .8rem; line-height: 1.55; }",
  "@media (min-width: 600px) { .ld09-root .ld09-strip { margin-top: 28px; } .ld09-tile { flex-direction: row; align-items: flex-start; text-align: left; gap: 14px; padding: 24px; } .ld09-tile-ico { width: 50px; height: 50px; } .ld09-tile-ico svg { font-size: 26px; } .ld09-tile-title { font-size: .98rem; } .ld09-tile-text { display: block; } }",

  "@media (max-height: 520px) { .ld09-content { padding-top: 12px; padding-bottom: 12px; } .ld09-brand { margin-bottom: 12px; } .ld09-brand img { height: 30px; } .ld09-eyebrow { display: none; } .ld09-steps { padding-top: 18px; } .ld09-root .ld09-strip { margin-top: 14px; } .ld09-tile { padding: 10px 6px; } .ld09-copy { margin-top: 12px; } }",
  ".ld09-copy { margin: 24px 0 0; text-align: center; color: #94a3b8; font-size: .73rem; }",

  /* session dialog */
  ".ld09-dlg-banner { background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%); padding: 24px 24px 20px; border-bottom: 3px solid #f59e0b; }",
  ".ld09-dlg-head { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }",
  ".ld09-dlg-ico { width: 42px; height: 42px; border-radius: 14px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; background: rgba(245,158,11,.16); border: 1px solid rgba(245,158,11,.4); color: #fbbf24; }",
  ".ld09-dlg-title { margin: 0; color: #fff; font-size: 1.05rem; font-weight: 800; line-height: 1.3; }",
  ".ld09-dlg-sub { margin: 2px 0 0; color: rgba(255,255,255,.72); font-size: .78rem; }",
  ".ld09-dlg-note { margin: 0; padding: 8px 16px; border-radius: 10px; background: rgba(255,255,255,.08); color: rgba(255,255,255,.88); font-size: .8rem; line-height: 1.5; }",
  ".ld09-dlg-body { padding: 16px 20px 4px; }",
  ".ld09-dlg-label { margin: 0 0 12px; color: #64748b; font-size: .72rem; font-weight: 700; letter-spacing: .8px; text-transform: uppercase; }",
  ".ld09-dlg .ld09-sess { display: flex; align-items: center; gap: 12px; padding: 12px; margin-bottom: 8px; border-radius: 14px; background: #f8fafc; box-shadow: none; transition: border-color .2s ease, box-shadow .2s ease; }",
  ".ld09-dlg .ld09-sess:hover { border-color: #f59e0b; box-shadow: 0 6px 14px -6px rgba(245,158,11,.35); }",
  ".ld09-sess-ico { width: 38px; height: 38px; border-radius: 12px; background: #fef3c7; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }",
  ".ld09-sess-info { flex: 1; min-width: 0; overflow-wrap: anywhere; }",
  ".ld09-sess-name { margin: 0; color: #0f172a; font-size: .87rem; font-weight: 700; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }",
  ".ld09-sess-meta { display: flex; align-items: center; gap: 4px; margin-top: 3px; color: #64748b; font-size: .73rem; min-width: 0; }",
  ".ld09-sess-meta span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }",
  ".ld09-dlg .ld09-signout { min-width: 84px; height: 2.75rem; font-size: .75rem; font-weight: 700; border-radius: 10px; flex-shrink: 0; }",
  ".ld09-dlg-foot { padding: 4px 20px 20px; }",
  ".ld09-dlg .ld09-cancel { width: 100%; height: 2.75rem; border-radius: 12px; font-weight: 700; color: #334155; }",
].join("\n");

function LoginDesign09({ connectionString }) {
  const path = connectionString;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { login } = useAuth();

  const reduce = useReducedMotion();

  const autoIdentifier = searchParams.get("identifier");
  const autoUsername = searchParams.get("UserName");
  const autoMobile = searchParams.get("mobile");
  const autoEmail = searchParams.get("email");
  const autoPassword = searchParams.get("password");
  const redirectPathFromQuery = searchParams.get("redirect");

  const [identifier, setIdentifier] = useState("");
  const [userPassword, setUserPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isCompanySelectionVisible, setIsCompanySelectionVisible] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [isAutoLogin, setIsAutoLogin] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  // Session limit picker
  const [sessionPickerData, setSessionPickerData] = useState(null);
  const [kickingSessionId, setKickingSessionId] = useState(null);

  // Visual-only: progressive step (1 = identify, 2 = verify) - step 3 is the company selection
  const [step, setStep] = useState(1);
  const currentStep = isCompanySelectionVisible ? 3 : step;
  const directionRef = useRef("fwd");
  const focusOnShowRef = useRef(false);
  const hasMountedRef = useRef(false);
  useEffect(() => { hasMountedRef.current = true; }, []);
  const identifierRef = useRef(null);
  const passwordRef = useRef(null);

  // Device fingerprint (created once, persisted in localStorage)
  const getDeviceId = () => {
    let did = localStorage.getItem("_did");
    if (!did) {
      if (window.crypto && typeof window.crypto.randomUUID === "function") {
        did = window.crypto.randomUUID();
      } else {
        // Fallback for HTTP (non-localhost) — crypto.randomUUID requires HTTPS
        did = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
        });
      }
      localStorage.setItem("_did", did);
    }
    return did;
  };

  const getBrowserName = () => {
    const ua = navigator.userAgent;
    if (ua.includes("Chrome") && !ua.includes("Edg")) return `Chrome on ${getOSName()}`;
    if (ua.includes("Firefox")) return `Firefox on ${getOSName()}`;
    if (ua.includes("Safari") && !ua.includes("Chrome")) return `Safari on ${getOSName()}`;
    if (ua.includes("Edg")) return `Edge on ${getOSName()}`;
    return `Browser on ${getOSName()}`;
  };

  const getOSName = () => {
    const ua = navigator.userAgent;
    if (ua.includes("Windows")) return "Windows";
    if (ua.includes("Mac")) return "macOS";
    if (ua.includes("Linux")) return "Linux";
    return "Unknown OS";
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins  = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days  = Math.floor(diff / 86400000);
    if (mins < 1)   return "just now";
    if (mins < 60)  return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const deviceIcon = (type) => {
    if (type === "mobile")  return <PhoneAndroidIcon sx={{ fontSize: 20, color: "#b45309" }} />;
    if (type === "tablet")  return <TabletIcon sx={{ fontSize: 20, color: "#b45309" }} />;
    if (type === "desktop") return <DesktopWindowsIcon sx={{ fontSize: 20, color: "#b45309" }} />;
    return <LaptopIcon sx={{ fontSize: 20, color: "#b45309" }} />;
  };

  const handleTogglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s?.trim());

  useEffect(() => {
    const handleMessage = (ev) => {
      try {
        const data = typeof ev.data === "string" ? JSON.parse(ev.data) : ev.data;
        if (!data || !data.type) return;
        if (data.type === "BIOMETRIC_LOGIN") {
          FN_HandleLogin(data.identifier, data.password);
        }
        if (data.type === "BIOMETRIC_FAILED") {
          setErrorMessage("Biometric authentication failed or cancelled.");
        }
        if (data.type === "BIOMETRIC_NOT_AVAILABLE") {
          setErrorMessage("Biometrics not available on this device.");
        }
      } catch (err) {
        console.warn("Error parsing message from native:", err);
      }
    };
    window.addEventListener("message", handleMessage);
    document.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
      document.removeEventListener("message", handleMessage);
    };
  }, []);

  const isLikelyMobile = (s) => {
    if (!s) return false;
    const cleaned = s.replace(/[\s\-\(\)\+]/g, "");
    const digits = cleaned.replace(/\D/g, "");
    const nonDigitCount = cleaned.replace(/\d/g, "").length;
    return (digits.length >= 6 && digits.length <= 15 && nonDigitCount === 0);
  };

  const determineIdentifierType = (id) => {
    const trimmed = id?.trim() || "";
    if (isEmail(trimmed)) return "email";
    if (isLikelyMobile(trimmed)) return "mobile";
    return "UserName";
  };

  useEffect(() => {
    const ai = autoIdentifier || autoUsername || autoMobile || autoEmail;
    if (ai && autoPassword) {
      setIsAutoLogin(true);
      setIdentifier(ai);
      setUserPassword(autoPassword);
      setStep(2);
      FN_HandleLogin(ai, autoPassword);
    }
  }, [autoIdentifier, autoUsername, autoMobile, autoEmail, autoPassword]);

  const FN_HandleLogin = async (overrideIdentifier, overridePassword, kickSessionId = null) => {
    setErrorMessage("");
    setIsLoading(true);
    const id = (overrideIdentifier ?? identifier ?? "").trim();
    const pwd = overridePassword ?? userPassword;

    if (!id || !pwd) {
      setErrorMessage("Please provide identifier and password.");
      setIsLoading(false);
      return;
    }

    try {
      const identifierType = determineIdentifierType(id);

      // Build device info
      let deviceInfo = {
        deviceId:   getDeviceId(),
        deviceName: getBrowserName(),
        platform:   navigator.platform,
        deviceType: "web",
      };
      if (window.electronAPI) {
        try { deviceInfo = await window.electronAPI.getDeviceInfo(); } catch (_) {}
      }

      const payload = {
        identifier: id,
        password: pwd,
        type: identifierType,
        deviceId:     deviceInfo.deviceId,
        deviceName:   deviceInfo.deviceName,
        platform:     deviceInfo.platform,
        deviceType:   deviceInfo.deviceType,
        kickSessionId: kickSessionId,
      };
      const response = await axios.post(`${path}/api/login`, payload,{ withCredentials: true });

      // Session limit reached — show device picker
      if (response.data.sessionLimitReached) {
        setSessionPickerData(response.data);
        setIsLoading(false);
        return;
      }

      if (!response.data.success) {
        setErrorMessage(response.data.message || "Invalid credentials.");
        return;
      }

      // Store session token for all future API requests
      if (response.data.sessionToken) {
        sessionStorage.setItem("sessionToken", response.data.sessionToken);
        axios.defaults.headers.common["x-session-token"] = response.data.sessionToken;
      }

      if (!overrideIdentifier && window.ReactNativeWebView?.postMessage) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({ type: "STORE_CREDENTIALS", identifier: id, password: pwd })
        );
      }

      const user = response.data.user;
      const companyRes = await axios.get(`${path}/api/companies?companyId=${user.CompId}`);

      if (!companyRes.data.success || !companyRes.data.companies?.length) {
        navigate("/registerCompany", { replace: true, state: { pendingUser: user, password: pwd } });
        return;
      }

      const currentLoginCompanyList = companyRes.data.companies;
      const actualSelectedCompany =
        currentLoginCompanyList.find((c) => c.companyId === user.companyId || c.companyId === user.CompId) ||
        currentLoginCompanyList[0];

      const allRes = await axios.get(`${path}/api/All-companies`, { params: { userId: user.UserId } });
      const allCompanies =
        allRes.data.success && allRes.data.data?.length ? allRes.data.data : currentLoginCompanyList;
      const serverIsSuperAdmin = Boolean(allRes.data.isSuperAdmin);

      await login(user, allCompanies, actualSelectedCompany, serverIsSuperAdmin);

      setCompanies(currentLoginCompanyList);
      setSelectedCompany(actualSelectedCompany);

      if (isAutoLogin || currentLoginCompanyList.length === 1) {
        FN_SelectCompany(actualSelectedCompany);
      } else {
        setIsCompanySelectionVisible(true);
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Login failed.");
      console.error("Login error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const FN_SelectCompany = (companyOverride = null) => {
    const company = companyOverride || selectedCompany;
    if (!company) {
      setErrorMessage("Please select a company.");
      return;
    }
    localStorage.setItem("selectedCompanyId", company.CompId);
    const redirectPath = redirectPathFromQuery || "/main/home";
    navigate(redirectPath);
  };

  const handleCapsLockCheck = (e) => {
    if (e.getModifierState) {
      setCapsLockOn(e.getModifierState("CapsLock"));
    }
  };

  // Visual-only step navigation
  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (currentStep === 1) {
      if (!identifier.trim()) {
        setErrorMessage("Please enter your username, email or mobile number.");
        return;
      }
      setErrorMessage("");
      directionRef.current = "fwd";
      focusOnShowRef.current = true;
      setStep(2);
      return;
    }
    setIsAutoLogin(false);
    FN_HandleLogin();
  };

  const handleBackToIdentify = () => {
    setErrorMessage("");
    setShowPassword(false);
    setUserPassword("");
    directionRef.current = "back";
    focusOnShowRef.current = true;
    setStep(1);
  };

  // Move keyboard focus into the field of the newly shown step (after the transition settles)
  const handlePanelShown = () => {
    if (!focusOnShowRef.current) return;
    focusOnShowRef.current = false;
    if (currentStep === 1) identifierRef.current?.focus();
    if (currentStep === 2) passwordRef.current?.focus();
  };

  /* ───────────── motion variants (all collapse to instant when reduced motion is on) ───────────── */
  const dur = (s) => (reduce ? 0 : s);
  const dir = directionRef.current === "back" ? -1 : 1;

  const pageV = {
    hidden: {},
    show: { transition: { staggerChildren: reduce ? 0 : 0.12, delayChildren: dur(0.04) } },
  };
  const itemV = {
    hidden: { opacity: 1, y: reduce ? 0 : 18 },
    show: { opacity: 1, y: 0, transition: { duration: dur(0.6), ease: EASE } },
  };
  const stripV = {
    hidden: { opacity: 1, y: reduce ? 0 : 18 },
    show: { opacity: 1, y: 0, transition: { duration: dur(0.6), ease: EASE, staggerChildren: reduce ? 0 : 0.1, delayChildren: dur(0.15) } },
  };
  const tileV = {
    hidden: { opacity: 1, y: reduce ? 0 : 12 },
    show: { opacity: 1, y: 0, transition: { duration: dur(0.5), ease: EASE } },
  };
  const panelV = {
    enter: (d) => ({ opacity: 1.25, x: reduce ? 0 : d * 24 }),
    center: { opacity: 1, x: 0, transition: { duration: dur(0.38), ease: EASE } },
  };

  const hoverLift = reduce ? undefined : { y: -2 };
  const tapPress = reduce ? undefined : { scale: 0.98 };

  const Panel = currentStep < 3 ? MotionForm : MotionDiv;

  const capsHelper = (id) => (
    <AnimatePresence initial={false}>
      {capsLockOn && (
        <motion.p key="caps" id={id} role="status" className="ld09-caps"
          initial={{ opacity: 1, y: reduce ? 0 : -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
          transition={{ duration: dur(0.2) }}>
          <WarningAmberIcon sx={{ fontSize: 14 }} />Caps Lock is ON
        </motion.p>
      )}
    </AnimatePresence>
  );

  const errorBox = (
    <AnimatePresence initial={false}>
      {errorMessage && (
        <motion.div key="err" style={{ overflow: "hidden" }}
          initial={{ opacity: 1, height: 0 }}
          animate={{ opacity: 1, height: "auto", x: reduce ? 0 : [0, -7, 7, -5, 5, 0] }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ opacity: { duration: dur(0.2) }, height: { duration: dur(0.25) }, x: { duration: dur(0.45), ease: "easeInOut" } }}>
          <div className="ld09-alert-wrap">
            <Alert variant="destructive" className="ld09-alert">
              <WarningAmberIcon sx={{ fontSize: 17, flexShrink: 0, mt: "1px" }} />
              <span>{errorMessage}</span>
            </Alert>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const biometricButton = typeof window !== "undefined" && window.isNativeApp && window.ReactNativeWebView ? (
    <MotionButton type="button" variant="outline" className="ld09-ghost" whileHover={hoverLift} whileTap={tapPress}
      onClick={() => window.ReactNativeWebView?.postMessage(JSON.stringify({ type: "REQUEST_BIOMETRIC" }))}>
      <FingerprintIcon sx={{ fontSize: 20 }} />
      Login with Biometric
    </MotionButton>
  ) : null;

  const identityPill = (editable) => (
    <Badge variant="outline" className="ld09-pill">
      <span className="ld09-pill-ico" aria-hidden="true" data-ok={editable ? "false" : "true"}>
        {editable ? <PersonOutlineIcon sx={{ fontSize: 18 }} /> : <CheckIcon sx={{ fontSize: 17 }} />}
      </span>
      <span className="ld09-pill-txt" title={identifier.trim()}>{identifier.trim()}</span>
      {editable && (
        <Button type="button" variant="ghost" className="ld09-change" onClick={handleBackToIdentify} aria-label="Change account">
          <EditOutlinedIcon sx={{ fontSize: 15 }} />
          Change
        </Button>
      )}
    </Badge>
  );

  return (
    <ThemeScope vars={themeVars} className="ld09-root">
      <style>{CSS}</style>

      {/* ── DOTTED BACKGROUND ── */}
      <div className="ld09-bg" aria-hidden="true">
        <div className="ld09-dots" />
        <motion.div className="ld09-dots-amber"
          animate={reduce ? { opacity: 0.22 } : { opacity: [0.12, 0.38, 0.12] }}
          transition={{ duration: 6, repeat: reduce ? 0 : Infinity, ease: "easeInOut" }} />
        <motion.div className="ld09-glow-a"
          animate={reduce ? undefined : { x: [0, -30, 0], y: [0, 22, 0], scale: [1, 1.07, 1] }}
          transition={{ duration: 0.01, repeat: 0, ease: "easeInOut" }} />
        <motion.div className="ld09-glow-b"
          animate={reduce ? undefined : { x: [0, 28, 0], y: [0, -24, 0], scale: [1, 1.08, 1] }}
          transition={{ duration: 0.01, repeat: 0, ease: "easeInOut" }} />
      </div>

      {/* ── CONTENT ── */}
      <motion.div className="ld09-content" variants={pageV} initial="hidden" animate="show">

        {/* Brand */}
        <motion.div className="ld09-brand" variants={itemV}>
          <img src="/RBLOGOBLUE.svg" alt="ReonBook" />
          <p className="ld09-eyebrow">Enterprise Accounting Platform</p>
        </motion.div>

        {/* ── Compact card ── */}
        <MotionCard className="ld09-card" variants={itemV}>
          <div className="ld09-accent" aria-hidden="true" />
          {isLoading && (
            <div className="ld09-bar" aria-hidden="true">
              <motion.div
                initial={{ x: "-110%" }}
                animate={reduce ? { x: "0%", width: "100%" } : { x: ["-110%", "320%"] }}
                transition={{ duration: 1.1, repeat: reduce ? 0 : Infinity, ease: "easeInOut" }} />
            </div>
          )}

          {/* Animated step indicator */}
          <div className="ld09-steps">
            <ol className="ld09-stepper" aria-label="Sign-in progress">
              {STEPS.map((s, i) => {
                const n = i + 1;
                const done = n < currentStep;
                const active = n === currentStep;
                const state = done ? "done" : active ? "active" : "todo";
                return (
                  <Fragment key={s.label}>
                    <li className="ld09-step" data-state={state} aria-current={active ? "step" : undefined}>
                      <motion.div className="ld09-node" initial={false}
                        animate={{
                          backgroundColor: done ? "#f59e0b" : active ? "#ffffff" : "#f1f5f9",
                          borderColor: done || active ? "#f59e0b" : "#e2e8f0",
                          color: done ? "#0f172a" : active ? "#b45309" : "#94a3b8",
                          scale: active && !reduce ? 1.08 : 1,
                        }}
                        transition={{ duration: dur(0.35), ease: EASE }}>
                        {active && !reduce && (
                          <motion.span className="ld09-ring" aria-hidden="true"
                            initial={{ scale: 1, opacity: 0.6 }} animate={{ scale: 1.75, opacity: 1 }}
                            transition={{ duration: 0.01, repeat: 0, ease: "easeOut" }} />
                        )}
                        {done ? (
                            <motion.span key="check" style={{ display: "flex" }}
                              initial={{ scale: reduce ? 1 : 0, rotate: reduce ? 0 : -90 }} animate={{ scale: 1, rotate: 0 }}
                              transition={{ type: "spring", stiffness: 420, damping: 18 }}>
                              <CheckIcon sx={{ fontSize: 18 }} />
                            </motion.span>
                          ) : (
                            <span key="icon" style={{ display: "flex" }}>{s.icon}</span>
                          )}
                      </motion.div>
                      <span className="ld09-step-label">{s.label}</span>
                      <span className="ld09-sr">{done ? " (completed)" : active ? " (current step)" : " (upcoming)"}</span>
                    </li>
                    {i < STEPS.length - 1 && (
                      <li className="ld09-conn" aria-hidden="true">
                        <motion.div className="ld09-conn-fill" initial={false}
                          animate={{ scaleX: done ? 1 : 0 }}
                          transition={{ duration: dur(0.55), ease: [0.65, 0, 0.35, 1] }} />
                      </li>
                    )}
                  </Fragment>
                );
              })}
            </ol>
          </div>

          <span className="ld09-sr" role="status" aria-live="polite">
            Step {currentStep} of 3: {STEPS[currentStep - 1].label}
          </span>

          {/* Step content */}
          <Panel
              key={currentStep}
              className="ld09-panel"
              custom={dir}
              variants={panelV}
              initial={hasMountedRef.current ? "enter" : false}
              animate="center"
              onAnimationComplete={(def) => { if (def === "center") handlePanelShown(); }}
              onSubmit={currentStep < 3 ? handleFormSubmit : undefined}
            >
              <CardHeader>
                <CardTitle role="heading" aria-level={1}>{STEP_TEXT[currentStep].title}</CardTitle>
                <CardDescription>{STEP_TEXT[currentStep].sub}</CardDescription>
              </CardHeader>

              <CardContent>
                {currentStep === 1 && (
                  <>
                    <div className="ld09-field">
                      <Label htmlFor="ld09-identifier">Username, Email or Mobile</Label>
                      <Input id="ld09-identifier" name="username" className="ld09-input" ref={identifierRef}
                        value={identifier} autoComplete="username" placeholder="Username, email or mobile"
                        autoCapitalize="none" autoCorrect="off" spellCheck="false"
                        aria-describedby={capsLockOn ? "ld09-caps-1" : undefined}
                        onChange={(e) => setIdentifier(e.target.value)}
                        onKeyUp={handleCapsLockCheck} onFocus={handleCapsLockCheck} onBlur={() => setCapsLockOn(false)} />
                      {capsHelper("ld09-caps-1")}
                    </div>

                    {errorBox}

                    <MotionButton type="submit" className="ld09-cta" whileHover={hoverLift} whileTap={tapPress}>
                      Continue
                      <ArrowForwardIcon sx={{ fontSize: 20 }} />
                    </MotionButton>

                    {biometricButton}
                  </>
                )}

                {currentStep === 2 && (
                  <>
                    {/* Visually hidden username so password managers can pair the credentials */}
                    <input type="text" name="username" value={identifier} autoComplete="username" readOnly tabIndex={-1} aria-hidden="true"
                      style={{ position: "absolute", opacity: 0, height: 0, width: 0, pointerEvents: "none" }} />

                    {identityPill(true)}

                    <div className="ld09-field" style={{ marginBottom: 12 }}>
                      <Label htmlFor="ld09-password">Password</Label>
                      <div className="ld09-pw">
                        <Input id="ld09-password" name="password" className="ld09-input" ref={passwordRef}
                          type={showPassword ? "text" : "password"} value={userPassword}
                          autoComplete="current-password" placeholder="Enter your password"
                          style={{ paddingRight: capsLockOn ? "5.6rem" : "3.2rem" }}
                          aria-describedby={capsLockOn ? "ld09-caps-2" : undefined}
                          onChange={(e) => setUserPassword(e.target.value)}
                          onKeyUp={handleCapsLockCheck} onFocus={handleCapsLockCheck} onBlur={() => setCapsLockOn(false)} />
                        {capsLockOn && <span className="ld09-capsicon" aria-hidden="true"><WarningAmberIcon sx={{ fontSize: 20 }} /></span>}
                        <Button type="button" variant="ghost" size="icon" className="ld09-eye"
                          onClick={handleTogglePasswordVisibility} onMouseDown={(e) => e.preventDefault()}
                          aria-label={showPassword ? "Hide password" : "Show password"} aria-pressed={showPassword}>
                          {showPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                        </Button>
                      </div>
                      {capsHelper("ld09-caps-2")}
                    </div>

                    <div className="ld09-row-end">
                      <Link to="/forgot-password" className="ld09-link" style={{ fontSize: "0.82rem" }}>
                        Forgot password?
                      </Link>
                    </div>

                    {errorBox}

                    <MotionButton type="submit" className="ld09-cta" disabled={isLoading}
                      whileHover={isLoading ? undefined : hoverLift} whileTap={isLoading ? undefined : tapPress}>
                      {isLoading
                        ? <><CircularProgress size={18} thickness={5} sx={{ color: "#78350f" }} />Signing in...</>
                        : "Sign In"}
                    </MotionButton>

                    {biometricButton}
                  </>
                )}

                {currentStep === 3 && (
                  <>
                    {identityPill(false)}

                    <div role="radiogroup" aria-label="Organizations" className="ld09-orgs">
                      {companies.map((c, i) => {
                        const checked = selectedCompany?.companyId === c.companyId;
                        return (
                          <motion.label key={c.companyId} className="ld09-org" data-checked={checked ? "true" : "false"}
                            initial={{ opacity: 1, y: reduce ? 0 : 10 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: reduce ? 0 : Math.min(i, 8) * 0.06, duration: dur(0.35), ease: EASE }}
                            whileHover={hoverLift} whileTap={reduce ? undefined : { scale: 0.99 }}>
                            <input type="radio" name="ld09-org" className="ld09-sr" value={String(c.companyId)}
                              checked={checked} onChange={() => setSelectedCompany(c)} />
                            <Card className="ld09-org-card">
                              <span className="ld09-org-ico" aria-hidden="true"><BusinessOutlinedIcon sx={{ fontSize: 20 }} /></span>
                              <span className="ld09-org-name">{c.companyName}</span>
                              <span className="ld09-org-check" aria-hidden="true"><CheckIcon sx={{ fontSize: 14 }} /></span>
                            </Card>
                          </motion.label>
                        );
                      })}
                    </div>
                    <p className="ld09-hint">
                      {companies.length} organization{companies.length !== 1 ? "s" : ""} available for your account
                    </p>

                    {errorBox}

                    <MotionButton type="button" className="ld09-cta" onClick={() => FN_SelectCompany()}
                      whileHover={hoverLift} whileTap={tapPress}>
                      Continue to Dashboard
                      <ArrowForwardIcon sx={{ fontSize: 20 }} />
                    </MotionButton>
                  </>
                )}
              </CardContent>

              <CardFooter>
                {currentStep === 1 && (
                  <>
                    <Separator style={{ marginTop: 24, background: "#f1f5f9" }} />
                    <p className="ld09-signup">
                      Don't have an account?{" "}
                      <Link to="/register" className="ld09-link">Sign up now</Link>
                    </p>
                  </>
                )}
                {currentStep === 2 && (
                  <Button type="button" variant="ghost" className="ld09-textbtn" style={{ marginTop: 12 }} onClick={handleBackToIdentify}>
                    <ArrowForwardIcon sx={{ fontSize: 16, transform: "rotate(180deg)" }} />
                    Use a different account
                  </Button>
                )}
              </CardFooter>
            </Panel>
        </MotionCard>

        {/* ── Trust strip ── */}
        <MotionCard className="ld09-strip" role="list" aria-label="Why teams trust ReonBook" variants={stripV}>
          {TRUST.map((t) => (
            <motion.div key={t.title} className="ld09-tile" role="listitem" variants={tileV}>
              <motion.div className="ld09-tile-ico" aria-hidden="true"
                whileHover={reduce ? undefined : { y: -3, scale: 1.06 }} transition={{ type: "spring", stiffness: 400, damping: 20 }}>
                {t.icon}
              </motion.div>
              <div style={{ minWidth: 0 }}>
                <p className="ld09-tile-title">{t.title}</p>
                <p className="ld09-tile-text">{t.text}</p>
              </div>
            </motion.div>
          ))}
        </MotionCard>

        <motion.p className="ld09-copy" variants={itemV}>
          <CopyrightIcon sx={{ fontSize: 12, verticalAlign: "middle", mr: 0.4 }} />
          {new Date().getFullYear()} Powered by{" "}
          {/* <a href="https://soltrack.co.in/" target="_blank" rel="noopener noreferrer" style={{ color: "#475569", fontWeight: 600, textDecoration: "none" }}> */}
            Soltrack Technologies Pvt Ltd
          {/* </a> */}
        </motion.p>
      </motion.div>

      {/* ── SESSION LIMIT PICKER MODAL ── */}
      <Dialog
        className="ld09-dlg"
        open={!!sessionPickerData}
        onClose={() => setSessionPickerData(null)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="ld09-session-title"
        slots={{ transition: Grow }}
        transitionDuration={{ enter: 320, exit: 180 }}
        slotProps={{
          backdrop: { sx: { backdropFilter: "blur(4px)", bgcolor: "rgba(11,18,32,0.6)" } },
          paper: { sx: { borderRadius: "22px", overflow: "hidden", m: { xs: 2, sm: 4 }, width: { xs: "calc(100% - 32px)", sm: "100%" } } },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <ThemeScope vars={themeVars}>
            {/* Top banner */}
            <div className="ld09-dlg-banner">
              <div className="ld09-dlg-head">
                <div className="ld09-dlg-ico"><DevicesIcon sx={{ fontSize: 22 }} /></div>
                <div>
                  <h2 id="ld09-session-title" className="ld09-dlg-title">Session Limit Reached</h2>
                  <p className="ld09-dlg-sub">
                    {sessionPickerData?.maxSessions} of {sessionPickerData?.maxSessions} allowed session{sessionPickerData?.maxSessions !== 1 ? "s" : ""} in use
                  </p>
                </div>
              </div>
              <p className="ld09-dlg-note">Sign out of one of the devices below to log in on this device.</p>
            </div>

            {/* Device list */}
            <div className="ld09-dlg-body">
              <p className="ld09-dlg-label">Active Sessions</p>

              {sessionPickerData?.activeSessions?.map((session, idx) => (
                <MotionCard key={session.id} className="ld09-sess"
                  initial={{ opacity: 1, y: reduce ? 0 : 12 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: reduce ? 0 : 0.1 + idx * 0.07, duration: dur(0.4), ease: EASE }}>
                  {/* Icon */}
                  <div className="ld09-sess-ico">{deviceIcon(session.device_type)}</div>

                  {/* Info */}
                  <div className="ld09-sess-info">
                    <p className="ld09-sess-name">{session.device_name || "Unknown Device"}</p>
                    <div className="ld09-sess-meta">
                      <AccessTimeIcon sx={{ fontSize: 12 }} />
                      <span>
                        {timeAgo(session.last_activity)}
                        {session.ip_address ? ` · ${session.ip_address}` : ""}
                      </span>
                    </div>
                  </div>

                  {/* Action */}
                  <MotionButton
                    type="button"
                    variant="destructive"
                    className="ld09-signout"
                    whileHover={reduce || kickingSessionId === session.id ? undefined : { scale: 1.04 }}
                    whileTap={reduce ? undefined : { scale: 0.96 }}
                    disabled={kickingSessionId === session.id}
                    onClick={async () => {
                      setKickingSessionId(session.id);
                      setSessionPickerData(null);
                      await FN_HandleLogin(identifier, userPassword, session.id);
                      setKickingSessionId(null);
                    }}
                  >
                    {kickingSessionId === session.id
                      ? <CircularProgress size={13} sx={{ color: "#fff" }} />
                      : "Sign Out"}
                  </MotionButton>
                </MotionCard>
              ))}
            </div>

            {/* Cancel */}
            <div className="ld09-dlg-foot">
              <Button type="button" variant="outline" className="ld09-cancel" onClick={() => setSessionPickerData(null)}>
                Cancel
              </Button>
            </div>
          </ThemeScope>
        </DialogContent>
      </Dialog>
    </ThemeScope>
  );
}

export default LoginDesign09;
